/**
 * The mouths of the reply being spoken (docs/NEW-UI/09), clause by clause as the voice schedules them, on the reply's
 * content clock (voice/playback.ts): what the recorded-mouths player reads.
 */
import type { PlaybackClock, SpokenClause } from '../voice/controller';
import type { Speaking } from './mouth-player';
import { FRAME_MS, type Mouth } from './visemes';

/** The mouths of a clause's PCM (int16, 24 kHz): in a worker in the kiosk (mouth-reader.ts), directly in tests. */
export type MouthReader = (pcm: ArrayBuffer) => Promise<Mouth[]>;

export class SpeechTrack {
  private turn: number | null = null;
  private readonly mouths: Mouth[] = [];

  constructor(private readonly read: MouthReader) {}

  async add(clause: SpokenClause): Promise<void> {
    if (clause.turn !== this.turn) {
      this.turn = clause.turn;
      this.mouths.length = 0;
    }
    const mouths = await this.read(clause.pcm);
    if (clause.turn !== this.turn) return; // a newer reply began while this one was read
    const at = Math.round(clause.atMs / FRAME_MS);
    for (let k = this.mouths.length; k < at; k++) this.mouths[k] = 'rest';
    mouths.forEach((mouth, i) => (this.mouths[at + i] = mouth));
  }

  /** For the player: this reply's mouths, read on the voice's clock while it plays this reply. */
  speaking(clock: () => PlaybackClock | null): Speaking {
    return {
      track: this.mouths,
      clockMs: () => {
        const now = clock();
        return now && now.turn === this.turn ? now.playedMs : null;
      },
    };
  }
}
